import React from 'react';
import './DropdownItem.css';
import { Icon, IconProps } from '../../icons';

export interface DropdownItemProps {
  children: React.ReactNode;
  href?: string;
  to?: string | object;
  leadingVisual?: IconProps['name'] | React.ReactNode;
  trailingVisual?: IconProps['name'] | React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  as?: 'button' | 'a' | 'router-link';
  target?: string;
  ariaLabel?: string;
  /**
   * Marks the item as the currently-active/selected option. Renders the
   * primary-colored left stripe and (when no `trailingVisual` is supplied)
   * a trailing check icon, mirroring `InputSelect`'s selected state.
   */
  selected?: boolean;
}

export const DropdownItem = React.memo(function DropdownItem({
  children,
  href,
  // `to` is currently not forwarded as we're not integrating router links yet
  to,
  leadingVisual,
  trailingVisual,
  onClick,
  as,
  target,
  ariaLabel,
  selected = false,
}: DropdownItemProps) {
  let Component: 'button' | 'a' = 'button';
  if (href || to) {
    Component = 'a';
  } else if (as) {
    Component = as === 'a' ? 'a' : 'button';
  }

  const effectiveTrailingVisual =
    trailingVisual ?? (selected ? ('Check' as IconProps['name']) : undefined);

  const props = {
    ...(href ? { href } : {}),
    ...(target ? { target } : {}),
    onClick,
    'aria-label': ariaLabel,
    'aria-selected': selected || undefined,
    'data-selected': selected || undefined,
  };

  const renderVisual = (
    visual: IconProps['name'] | React.ReactNode,
    position: 'leading' | 'trailing',
  ) => {
    if (!visual) return null;
    if (typeof visual === 'string') {
      return (
        <Icon
          name={visual as IconProps['name']}
          className={`dropdown-item__icon dropdown-item__icon--${position}`}
        />
      );
    }
    if (React.isValidElement(visual)) {
      const el = visual as React.ReactElement<{ className?: string }>; // narrow props used
      return React.cloneElement(el, {
        className: `dropdown-item__icon dropdown-item__icon--${position} ${
          el.props.className || ''
        }`.trim(),
        'aria-hidden': true,
      } as Partial<typeof el.props> & { 'aria-hidden': true });
    }
    return null;
  };

  return (
    <li className="dropdown-item">
      <Component className="dropdown-item__content" {...props}>
        {renderVisual(leadingVisual, 'leading')}
        <span>{children}</span>
        {renderVisual(effectiveTrailingVisual, 'trailing')}
      </Component>
    </li>
  );
});
